import { useEffect, useRef } from 'react';
import { getObjectKeys } from '../../../common/utils/getObjectKeys';

/**
 * This hook can be used to determine why a component/hook has been updated.
 * Can be useful for debugging stuff.
 * Source: https://usehooks.com/useWhyDidYouUpdate/
 * Changed it around a little to support typescript
 */
export function useWhyDidYouUpdate<T extends object>(name: string, props: T) {
    // Get a mutable ref object where we can store props ...
    // ... for comparison next time this hook runs.
    const previousProps = useRef<T | null>(null);

    useEffect(() => {
        if (previousProps.current) {
            // Get all keys from previous and current props
            const allKeys = getObjectKeys({ ...previousProps.current, ...props });

            // Use this object to keep track of changed props
            const changesObj: { [Key in keyof T]?: { from: T[Key]; to: T[Key] } } = {};

            // Iterate through keys
            allKeys.forEach((key) => {
                // If previous is different from current
                if (previousProps.current![key] !== props[key]) {
                    // Add to changesObj
                    changesObj[key] = {
                        from: previousProps.current![key],
                        to: props[key],
                    };
                }
            });

            // If changesObj not empty then output to console
            if (Object.keys(changesObj).length) {
                console.log('[why-did-you-update]', name, changesObj);
            }
        }

        // Finally update previousProps with current props for next hook call
        previousProps.current = props;
    });
}
